import type { PaletteMode, Shadows } from '@mui/material/styles'
import { createTheme, alpha } from '@mui/material/styles'

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true
  }
}
declare module '@mui/material/styles/createPalette' {
  interface ColorRange {
    50: string
    100: string
    200: string
    300: string
    400: string
    500: string
    600: string
    700: string
    800: string
    900: string
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string
  }
}

const defaultTheme = createTheme()

const customShadows: Shadows = [...defaultTheme.shadows]

export const blue = {
  100: 'hsl(210, 100%, 92%)',
  200: 'hsl(210, 100%, 80%)',
  300: 'hsl(210, 100%, 65%)',
  400: 'hsl(210, 98%, 48%)',
  50: 'hsl(210, 100%, 95%)',
  500: 'hsl(210, 98%, 42%)',
  600: 'hsl(210, 98%, 55%)',
  700: 'hsl(210, 100%, 35%)',
  800: 'hsl(210, 100%, 16%)',
  900: 'hsl(210, 100%, 21%)'
}

export const gray = {
  100: 'hsl(220, 30%, 94%)',
  200: 'hsl(220, 20%, 88%)',
  300: 'hsl(220, 20%, 80%)',
  400: 'hsl(220, 20%, 65%)',
  50: 'hsl(220, 35%, 97%)',
  500: 'hsl(220, 20%, 42%)',
  600: 'hsl(220, 20%, 35%)',
  700: 'hsl(220, 20%, 25%)',
  800: 'hsl(220, 30%, 6%)',
  900: 'hsl(220, 35%, 3%)'
}

export const primary = {
  100: 'hsl(157, 37%, 79%)',
  200: 'hsl(156, 37%, 67%)',
  300: 'hsl(156, 35%, 55%)',
  400: 'hsl(156, 38%, 46%)',
  50: 'hsl(158, 38%, 92%)',
  500: 'hsl(154, 44%, 40%)',
  600: 'hsl(153, 44%, 37%)',
  700: 'hsl(151, 44%, 32%)',
  800: 'hsl(150, 46%, 28%)',
  900: 'hsl(146, 49%, 20%)'
}
export const green = {
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  50: 'hsl(120, 80%, 98%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)'
}
export const secondary = {
  100: '#f8c2d5',
  200: '#f59bb9',
  300: '#f2759e',
  400: '#ee5c88',
  50: '#fce7ee',
  500: '#ec4a74',
  600: '#db4570',
  700: '#c4406a',
  800: '#ae3c64',
  900: '#873459'
}

export const red = {
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  50: 'hsl(0, 100%, 97%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)'
}
export const orange = {
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  50: 'hsl(45, 100%, 97%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)'
}

export const getDesignTokens = (mode: PaletteMode) => {
  customShadows[1] =
    mode === 'dark'
      ? 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px'
      : 'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px'

  return {
    palette: {
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
        ...(mode === 'dark' && {
          hover: alpha(gray[600], 0.2),
          selected: alpha(gray[600], 0.3)
        })
      },
      background: {
        default: 'hsl(0, 0%, 99%)',
        paper: 'hsl(220, 35%, 97%)',
        ...(mode === 'dark' && { default: gray[900], paper: 'hsl(220, 30%, 7%)' })
      },
      divider: mode === 'dark' ? alpha(gray[700], 0.6) : alpha(gray[300], 0.4),
      error: {
        dark: red[800],
        light: red[300],
        main: red[400],
        ...(mode === 'dark' && {
          dark: red[700],
          light: red[400],
          main: red[500]
        })
      },
      grey: {
        ...gray
      },
      info: {
        contrastText: gray[50],
        dark: blue[600],
        light: blue[100],
        main: blue[300],
        ...(mode === 'dark' && {
          contrastText: blue[300],
          dark: blue[900],
          light: blue[500],
          main: blue[700]
        })
      },
      mode,
      primary: {
        contrastText: blue[50],
        dark: blue[700],
        light: blue[200],
        main: blue[400],
        ...(mode === 'dark' && {
          contrastText: blue[50],
          dark: blue[700],
          light: blue[300],
          main: blue[400]
        })
      },
      success: {
        dark: green[800],
        light: green[300],
        main: green[400],
        ...(mode === 'dark' && {
          dark: green[700],
          light: green[400],
          main: green[500]
        })
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
        ...(mode === 'dark' && { primary: 'hsl(0, 0%, 100%)', secondary: gray[400] })
      },
      warning: {
        dark: orange[800],
        light: orange[300],
        main: orange[400],
        ...(mode === 'dark' && {
          dark: orange[700],
          light: orange[400],
          main: orange[500]
        })
      }
    },
    shadows: customShadows,
    shape: {
      borderRadius: 8
    },
    typography: {
      body1: {
        fontSize: defaultTheme.typography.pxToRem(14)
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400
      },
      fontFamily: 'Inter, sans-serif',
      h1: {
        fontSize: defaultTheme.typography.pxToRem(48),
        fontWeight: 600,
        letterSpacing: -0.5,
        lineHeight: 1.2
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(36),
        fontWeight: 600,
        lineHeight: 1.2
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(30),
        lineHeight: 1.2
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 600,
        lineHeight: 1.5
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18)
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 500
      }
    }
  }
}

export const colorSchemes = {
  dark: {
    palette: {
      action: {
        hover: alpha(gray[600], 0.2),
        selected: alpha(gray[600], 0.3)
      },
      background: {
        default: gray[900],
        paper: 'hsl(220, 30%, 7%)'
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
      divider: alpha(gray[700], 0.6),
      error: {
        dark: red[700],
        light: red[400],
        main: red[500]
      },
      grey: {
        ...gray
      },
      info: {
        contrastText: blue[300],
        dark: blue[900],
        light: blue[500],
        main: blue[700]
      },
      primary: {
        contrastText: primary[50],
        dark: primary[700],
        light: primary[500],
        main: primary[600]
      },
      secondary: {
        contrastText: secondary[50],
        dark: secondary[700],
        light: secondary[800],
        main: secondary[900]
      },
      success: {
        dark: green[700],
        light: green[400],
        main: green[500]
      },
      text: {
        primary: 'hsl(0, 0%, 100%)',
        secondary: gray[400]
      },
      warning: {
        dark: orange[700],
        light: orange[400],
        main: orange[500]
      }
    }
  },
  light: {
    palette: {
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`
      },
      background: {
        default: 'hsl(0, 0%, 99%)',
        paper: 'hsl(220, 35%, 97%)'
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
      divider: alpha(gray[300], 0.4),
      error: {
        dark: red[800],
        light: red[300],
        main: red[400]
      },
      grey: {
        ...gray
      },
      info: {
        contrastText: gray[50],
        dark: primary[600],
        light: primary[100],
        main: blue[300]
      },
      primary: {
        contrastText: blue[50],
        dark: primary[700],
        light: primary[400],
        main: primary[600]
      },
      secondary: {
        contrastText: secondary[50],
        dark: secondary[700],
        light: secondary[800],
        main: secondary[900]
      },
      success: {
        dark: green[800],
        light: green[300],
        main: green[400]
      },
      text: {
        primary: gray[600],
        secondary: gray[600],
        warning: orange[400]
      },
      warning: {
        dark: orange[800],
        light: orange[300],
        main: orange[400]
      }
    }
  }
}

export const typography = {
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14)
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400
  },
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    '"Roboto", sans-serif',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
    '"Inter", sans-serif'
  ].join(','),
  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    letterSpacing: -0.5,
    lineHeight: 1.2
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18)
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500
  }
}

export const shape = {
  borderRadius: 8
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const defaultShadows: Shadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2)
]
export const shadows = defaultShadows
